import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { finishedGoodAPI } from '../utils/api';
import { Layers, Plus, Edit2, X, AlertTriangle, Search, ChevronDown, ChevronUp, Warehouse, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import useAuthStore from '../store/authStore';
import { Thumb, ImagePicker } from '../components/common/ImageZoom';
import { localName, fmt, money, apiError, STOCK_BADGE } from '../utils/helpers';
import StatusFilter from '../components/common/StatusFilter';
import SectionHero from '../components/common/SectionHero';

const emptyForm = { name: '', name_en: '', name_ru: '', unit_rolls: 'rolls', unit_sqm: 'm2', low_stock_threshold_rolls: 0, description: '' };

export default function FinishedGoods() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { user } = useAuthStore();
  const canEdit = ['admin', 'manager', 'production_manager'].includes(user?.role);

  const [goods, setGoods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expanded, setExpanded] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [image, setImage] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    finishedGoodAPI.list()
      .then(r => setGoods(r.data))
      .catch(() => toast.error(t('error')))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const q = search.trim().toLowerCase();
  const filtered = goods.filter(g =>
    (!q || [g.name, g.name_en, g.name_ru, g.description].some(v => (v || '').toLowerCase().includes(q))) &&
    (statusFilter === 'all' || g.stock_status === statusFilter)
  );
  const lowCount = goods.filter(g => g.stock_status !== 'in_stock').length;
  const counts = { all: goods.length, in_stock: 0, low: 0, out: 0 };
  goods.forEach(g => { counts[g.stock_status] = (counts[g.stock_status] || 0) + 1; });

  const toggleExpand = id => setExpanded(e => ({ ...e, [id]: !e[id] }));
  const openCreate = () => { setEditing(null); setForm(emptyForm); setImage(null); setShowModal(true); };
  const openEdit = g => {
    setEditing(g);
    setForm({ name: g.name, name_en: g.name_en || '', name_ru: g.name_ru || '', unit_rolls: g.unit_rolls, unit_sqm: g.unit_sqm,
      low_stock_threshold_rolls: g.low_stock_threshold_rolls, description: g.description || '' });
    setImage(g.image_path || null);
    setShowModal(true);
  };
  const closeModal = () => { setShowModal(false); setEditing(null); setForm(emptyForm); setImage(null); };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error(t('name_required'));
    setSaving(true);
    try {
      let id = editing?.id;
      if (editing) await finishedGoodAPI.update(id, form);
      else id = (await finishedGoodAPI.create(form)).data.id;
      if (image instanceof File) await finishedGoodAPI.uploadImage(id, image);
      else if (editing?.image_path && !image) await finishedGoodAPI.removeImage(id);
      toast.success(t('saved'));
      closeModal(); load();
    } catch (e) { toast.error(apiError(e, t)); } finally { setSaving(false); }
  };

  const handleDelete = async g => {
    if (!window.confirm(t('confirm_delete'))) return;
    try { await finishedGoodAPI.delete(g.id); toast.success(t('deleted')); load(); } catch (e) { toast.error(apiError(e, t)); }
  };

  const totalRolls = goods.reduce((s, g) => s + g.total_rolls, 0);
  const totalSqm = goods.reduce((s, g) => s + g.total_sqm, 0);
  const cols = canEdit ? 8 : 7;

  return (
    <div className="space-y-5">
      <SectionHero title={t('finished_goods')} subtitle={<>{goods.length} {t('products')}{lowCount > 0 ? ` · ${lowCount} ` + t('low_stock') : ''}</>}>
        {canEdit && <button className="btn btn-primary" onClick={openCreate}><Plus size={16} /> {t('add_product')}</button>}
      </SectionHero>

      {!loading && goods.length > 0 && (
        <div className="grid-3">
          <div className="stat-card"><div><p className="stat-label">{t('products')}</p><p className="stat-value">{goods.length}</p></div>
            <div className="stat-icon" style={{ background: 'var(--accent-dim)' }}><Layers size={20} style={{ color: 'var(--accent)' }} /></div></div>
          <div className="stat-card"><div><p className="stat-label">{t('total_rolls')}</p><p className="stat-value">{fmt(totalRolls)}</p></div>
            <div className="stat-icon" style={{ background: 'var(--green-dim)' }}><Layers size={20} style={{ color: 'var(--green)' }} /></div></div>
          <div className="stat-card"><div><p className="stat-label">{t('total_sqm')}</p><p className="stat-value">{fmt(totalSqm)}</p></div>
            <div className="stat-icon" style={{ background: 'var(--yellow-dim)' }}><Layers size={20} style={{ color: 'var(--yellow)' }} /></div></div>
        </div>
      )}

      {lowCount > 0 && (
        <div className="alert alert-warning"><AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} /><span>{lowCount} {t('low_stock_alert')}</span></div>
      )}

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: '1 1 200px' }}>
            <Search size={15} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
            <input className="input" style={{ border: 'none', background: 'transparent', padding: 0, fontSize: 13 }} placeholder={t('search') + '...'} value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <StatusFilter value={statusFilter} onChange={setStatusFilter} counts={counts} t={t} />
        </div>

        {loading ? <div className="loading-center"><div className="spinner" /></div>
          : filtered.length === 0 ? <div className="empty-state"><Layers size={32} style={{ color: 'var(--text-muted)', margin: '0 auto 8px' }} /><p>{t('no_data')}</p></div>
          : (
            <div className="table-wrap">
              <table>
                <thead><tr>
                  <th style={{ width: 32 }}></th>
                  <th style={{ width: 56 }}>{t('image')}</th>
                  <th>{t('product')}</th>
                  <th>{t('total_rolls')}</th>
                  <th className="hide-mobile">{t('total_sqm')}</th>
                  <th className="hide-mobile">{t('price_per_roll')}</th>
                  <th>{t('status')}</th>
                  {canEdit && <th>{t('actions')}</th>}
                </tr></thead>
                <tbody>
                  {filtered.map(g => (
                    <React.Fragment key={g.id}>
                      <tr style={{ cursor: 'pointer' }} onClick={() => toggleExpand(g.id)}>
                        <td>{expanded[g.id] ? <ChevronUp size={14} style={{ color: 'var(--text-muted)' }} /> : <ChevronDown size={14} style={{ color: 'var(--text-muted)' }} />}</td>
                        <td><Thumb src={g.image_path} alt={g.name} /></td>
                        <td>
                          <div style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{localName(g, lang)}</div>
                          {g.description && <div className="text-xs truncate" style={{ color: 'var(--text-muted)', maxWidth: 260 }}>{g.description}</div>}
                        </td>
                        <td style={{ fontFamily: 'monospace' }}>{fmt(g.total_rolls)}</td>
                        <td className="hide-mobile" style={{ fontFamily: 'monospace' }}>{fmt(g.total_sqm)}</td>
                        <td className="hide-mobile" style={{ color: 'var(--green)' }}>{g.current_price != null ? money(g.current_price) : '—'}</td>
                        <td><span className={`badge ${STOCK_BADGE[g.stock_status]}`}>{t('stock_' + g.stock_status)}</span></td>
                        {canEdit && (
                          <td onClick={e => e.stopPropagation()}>
                            <div className="flex gap-1">
                              <button className="btn btn-ghost btn-sm btn-icon" onClick={() => openEdit(g)}><Edit2 size={13} /></button>
                              <button className="btn btn-ghost btn-sm btn-icon" style={{ color: '#f87171' }} onClick={() => handleDelete(g)}><Trash2 size={13} /></button>
                            </div>
                          </td>
                        )}
                      </tr>
                      {expanded[g.id] && (
                        <tr><td colSpan={cols} style={{ background: 'rgba(0,0,0,0.2)', padding: '12px 20px' }}>
                          <p className="text-xs font-semibold mb-2 flex items-center gap-1" style={{ color: 'var(--text-muted)' }}><Warehouse size={11} /> {t('stock_by_warehouse')}</p>
                          <div className="flex flex-wrap gap-2">
                            {g.stocks.map(s => (
                              <div key={s.warehouse_id} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, padding: '6px 12px', fontSize: 12 }}>
                                <span style={{ color: 'var(--text-secondary)', marginInlineEnd: 6 }}>{s.warehouse_name}</span>
                                <strong style={{ color: 'var(--text-primary)' }}>{fmt(s.quantity_rolls)} {t('rolls')}</strong>
                                <span style={{ color: 'var(--text-muted)', marginInlineStart: 4 }}>/ {fmt(s.quantity_sqm)} {t('sqm')}</span>
                              </div>
                            ))}
                          </div>
                          {(g.name_en || g.name_ru) && (
                            <p className="text-xs mt-2" style={{ color: 'var(--text-muted)' }}>
                              {g.name} {g.name_en ? ` · EN: ${g.name_en}` : ''}{g.name_ru ? ` · RU: ${g.name_ru}` : ''}
                            </p>
                          )}
                          {g.description && <p className="text-xs mt-2" style={{ color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>{g.description}</p>}
                        </td></tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="card-title">{editing ? t('edit_product') : t('add_product')}</h2>
              <button className="btn btn-ghost btn-icon" onClick={closeModal}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">{t('product_name')} *</label>
                <input className="input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">{t('name_en')}</label><input className="input" dir="ltr" value={form.name_en} onChange={e => setForm(f => ({ ...f, name_en: e.target.value }))} /></div>
                <div className="form-group"><label className="form-label">{t('name_ru')}</label><input className="input" dir="ltr" value={form.name_ru} onChange={e => setForm(f => ({ ...f, name_ru: e.target.value }))} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">{t('unit_rolls')}</label><input className="input" value={form.unit_rolls} onChange={e => setForm(f => ({ ...f, unit_rolls: e.target.value }))} /></div>
                <div className="form-group"><label className="form-label">{t('unit_sqm')}</label><input className="input" value={form.unit_sqm} onChange={e => setForm(f => ({ ...f, unit_sqm: e.target.value }))} /></div>
              </div>
              <div className="form-group">
                <label className="form-label">{t('low_stock_threshold')} ({t('rolls')})</label>
                <input className="input" type="number" min="0" step="any" value={form.low_stock_threshold_rolls} onChange={e => setForm(f => ({ ...f, low_stock_threshold_rolls: parseFloat(e.target.value) || 0 }))} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('description')}</label>
                <textarea className="input" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} style={{ minHeight: 64 }} />
              </div>
              <ImagePicker label={t('image')} value={image} onChange={setImage} onRemove={() => setImage(null)} />
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeModal}>{t('cancel')}</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? t('loading') : t('save')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
